import { useEffect, useMemo, useRef } from 'react';

import maplibregl from 'maplibre-gl';

import type { ExpressionSpecification } from '@maplibre/maplibre-gl-style-spec';

import { useStore } from '../store';

import { applyFilter } from '../filters/filterEngine';
import { applyVesselFilter } from '../filters/vesselFilterEngine';

import type { RichAircraft } from '../data/enrich';
import type { RichVessel } from '../data/vesselEnrich';

import { globalFilterForClasses, type LiveState } from '../data/useLiveStates';
import type { LiveVesselsState } from '../data/useLiveVessels';
import type { SelectedTrackState } from '../data/useSelectedAircraftTrack';
import { mergeSelectedTrack } from '../data/useSelectedAircraftTrack';

import { fetchTrace } from '../data/apiClient';

import {
  buildAircraftGeoJSON,
  buildAircraftAltitudeGeoJSON,
  buildFlightPathGeoJSON,
  loadPlaneIcons,
  ICON_IMAGE_EXPR,
  AIRCRAFT_COLOR_EXPR,
  ALTITUDE_HEIGHT_EXPR,
  ALTITUDE_CAP_SLICE_EXPR,
} from './aircraftSource';
import { ICON_SIZE_EXPR } from './aircraftIcons';

import { appendTrail, traceToTrail, trailToGeoJSON, type TrailPoint } from './flightTrail';

import { mapBridge, ALTITUDE_LAYER_IDS, MAP_MAX_PITCH, MAP_TOP_DOWN_PITCH, VESSEL_LAYER_IDS } from './mapBridge';
import {
  buildVesselsGeoJSON,
  buildVesselPathsGeoJSON,
  buildVesselLabelsGeoJSON,
  capVesselsForZoom,
  appendVesselTrail,
  vesselTrailToGeoJSON,
  type VesselTrailPoint,
} from './vesselSource';
import {
  loadVesselIcons,
  VESSEL_ICON_IMAGE_EXPR,
  VESSEL_COLOR_EXPR,
  VESSEL_ICON_SIZE_EXPR,
} from './vesselIcons';
import { createAircraftModelLayer } from './aircraftModelLayer';
import { ensureTerrainAndSky, mapStyle, MAP_TEXT_FONTS } from './basemap';
import { StarrySky } from './StarrySky';

function mergeVesselLabelText(
  vessels: GeoJSON.FeatureCollection,
  labels: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection {
  const labelById = new Map<string, string>();
  for (const feature of labels.features) {
    const id = feature.properties?.['id'];
    const label = feature.properties?.['label'];
    if (typeof id === 'string' && typeof label === 'string') labelById.set(id, label);
  }
  return {
    type: 'FeatureCollection',
    features: vessels.features.map((feature) => {
      const id = feature.properties?.['id'];
      const label = typeof id === 'string' ? labelById.get(id) : undefined;
      return label
        ? { ...feature, properties: { ...feature.properties, label } }
        : feature;
    }),
  };
}



interface Props {

  live: LiveState;

  liveVessels: LiveVesselsState;

  selectedTrack: SelectedTrackState;

}






export function FlightMap({ live, liveVessels, selectedTrack }: Props) {

  const containerRef = useRef<HTMLDivElement>(null);

  const mapRef = useRef<maplibregl.Map | null>(null);



  const filter = useStore((s) => s.filter);
  const vesselFilter = useStore((s) => s.vesselFilter);

  const selectedIcao = useStore((s) => s.selectedIcao);
  const selectedVesselId = useStore((s) => s.selectedVesselId);

  const setSelected = useStore((s) => s.setSelected);
  const setSelectedVessel = useStore((s) => s.setSelectedVessel);

  const preferLabelCallsign = useStore((s) => s.preferLabelCallsign);
  const focusIcao = useStore((s) => s.focusIcao);
  const focusPoint = useStore((s) => s.focusPoint);
  const focusSeq = useStore((s) => s.focusSeq);
  const clearFocus = useStore((s) => s.clearFocus);



  const filteredRef = useRef<RichAircraft[]>([]);
  const filteredVesselsRef = useRef<RichVessel[]>([]);

  const selectedRef = useRef<string | null>(null);
  const preferLabelCallsignRef = useRef(preferLabelCallsign);
  const pick3DRef = useRef<(map: maplibregl.Map, point: maplibregl.PointLike) => string | null>(() => null);
  const hover3DRef = useRef<(map: maplibregl.Map, point: maplibregl.PointLike) => boolean>(() => false);

  const snapshotTimeRef = useRef<number>(0);
  const vesselSnapshotTimeRef = useRef<number>(0);

  const rafRef = useRef<number | null>(null);

  const lastPaintRef = useRef<number>(0);

  const trailRef = useRef<TrailPoint[]>([]);
  const vesselTrailRef = useRef<VesselTrailPoint[]>([]);
  const traceAbortRef = useRef<AbortController | null>(null);
  const traceRequestRef = useRef(0);
  const selectedOnGroundRef = useRef<boolean | null>(null);
  const setViewRef = useRef(live.setView);
  const setVesselViewRef = useRef(liveVessels.setView);
  const filterRef = useRef(filter);
  const selectedVesselRef = useRef(selectedVesselId);

  useEffect(() => {
    setViewRef.current = live.setView;
  }, [live.setView]);
  useEffect(() => {
    setVesselViewRef.current = liveVessels.setView;
  }, [liveVessels.setView]);
  useEffect(() => {
    filterRef.current = filter;
  }, [filter]);
  useEffect(() => {
    selectedVesselRef.current = selectedVesselId;
  }, [selectedVesselId]);

  const apiFilterMode = globalFilterForClasses(filter.classes);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const b = map.getBounds();
    const view = {
      bbox: { minLon: b.getWest(), minLat: b.getSouth(), maxLon: b.getEast(), maxLat: b.getNorth() },
      zoom: map.getZoom(),
      globalFilter: apiFilterMode,
    };
    live.setView(view);
    liveVessels.setView(view);
  }, [apiFilterMode, live.setView, liveVessels.setView]);



  const selectedTrackRef = useRef(selectedTrack);
  useEffect(() => {
    selectedTrackRef.current = selectedTrack;
  }, [selectedTrack]);

  const displayAircraft = useMemo(() => {
    const merged = mergeSelectedTrack(live.aircraft, selectedTrack.aircraft);
    return merged;
  }, [live.aircraft, selectedTrack.aircraft]);

  const filtered = useMemo(() => {
    if (!mapBridge.getAircraftEnabled()) return [];

    const list = applyFilter(displayAircraft, filter);

    if (!selectedIcao) return list;

    if (list.some((a) => a.icao === selectedIcao)) return list;

    const sel = displayAircraft.find((a) => a.icao === selectedIcao);

    return sel ? [...list, sel] : list;

  }, [displayAircraft, filter, selectedIcao]);

  const filteredVessels = useMemo(() => {
    const list = applyVesselFilter(liveVessels.vessels, vesselFilter);

    if (!selectedVesselId) return list;

    if (list.some((v) => v.id === selectedVesselId)) return list;

    const sel = liveVessels.vessels.find((v) => v.id === selectedVesselId);

    return sel ? [...list, sel] : list;

  }, [liveVessels.vessels, vesselFilter, selectedVesselId]);



  useEffect(() => {

    filteredRef.current = filtered;

  }, [filtered]);

  useEffect(() => {

    filteredVesselsRef.current = filteredVessels;

  }, [filteredVessels]);

  useEffect(() => {

    selectedRef.current = selectedIcao;

  }, [selectedIcao]);

  useEffect(() => {
    preferLabelCallsignRef.current = preferLabelCallsign;
  }, [preferLabelCallsign]);



  useEffect(() => {

    if (live.data) snapshotTimeRef.current = live.data.time;
    else if (selectedTrack.fetchedAtSec > 0) snapshotTimeRef.current = selectedTrack.fetchedAtSec;

  }, [live.data, selectedTrack.fetchedAtSec]);

  useEffect(() => {

    if (liveVessels.data) vesselSnapshotTimeRef.current = liveVessels.data.time;

  }, [liveVessels.data]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map?.getLayer('flight-trail')) return;
    const selected = selectedIcao ? live.aircraft.find((a) => a.icao === selectedIcao) : null;
    const vip = selected?.specialKind === 'presidential' || selected?.specialKind === 'vip';
    map.setPaintProperty('flight-trail', 'line-color', selected?.specialKind === 'presidential' ? '#fde047' : vip ? '#fbbf24' : '#7dd3fc');
    map.setPaintProperty('flight-trail-glow', 'line-color', selected?.specialKind === 'presidential' ? '#fde047' : vip ? '#fbbf24' : '#38bdf8');
    map.setPaintProperty('flight-trail', 'line-width', selected?.specialKind === 'presidential' ? 4 : vip ? 3.25 : 2.5);
    map.setPaintProperty('flight-trail-glow', 'line-width', selected?.specialKind === 'presidential' ? 10 : vip ? 8 : 6);
    map.setPaintProperty('flight-trail-glow', 'line-opacity', vip ? 0.32 : 0.2);
  }, [selectedIcao, live.aircraft]);

  // One-shot camera move when the user explicitly focuses an aircraft.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !focusIcao) return;
    const ac = live.aircraft.find((a) => a.icao === focusIcao);
    const lon = focusPoint?.lon ?? ac?.lon;
    const lat = focusPoint?.lat ?? ac?.lat;
    if (lat == null || lon == null) {
      clearFocus();
      return;
    }
    map.easeTo({
      center: [lon, lat],
      zoom: Math.max(map.getZoom(), 8.5),
      duration: 900,
      essential: true,
    });
    clearFocus();
  }, [focusSeq, clearFocus]);



  // Load the selected aircraft's trace for the current flight leg, then live samples extend it.
  useEffect(() => {
    const reqId = ++traceRequestRef.current;
    traceAbortRef.current?.abort();
    trailRef.current = [];
    selectedOnGroundRef.current = null;
    const src = mapRef.current?.getSource('flight-trail') as maplibregl.GeoJSONSource | undefined;
    src?.setData(trailToGeoJSON([]));

    if (!selectedIcao) return;

    const ac = live.aircraft.find((a) => a.icao === selectedIcao);
    if (ac?.onGround) {
      selectedOnGroundRef.current = true;
      return;
    }

    const ctrl = new AbortController();
    traceAbortRef.current = ctrl;

    fetchTrace(selectedIcao, ctrl.signal)
      .then((trace) => {
        if (ctrl.signal.aborted || reqId !== traceRequestRef.current) return;
        trailRef.current = traceToTrail(trace.points);
        const traceSrc = mapRef.current?.getSource('flight-trail') as maplibregl.GeoJSONSource | undefined;
        traceSrc?.setData(trailToGeoJSON(trailRef.current));
      })
      .catch(() => {
        // Some aircraft have no public trace yet; live samples below still create a trail.
      })
      .finally(() => {
        if (traceAbortRef.current === ctrl) traceAbortRef.current = null;
      });

    return () => ctrl.abort();
  }, [selectedIcao]);

  // Append live position samples for the selected aircraft; reset when grounded.
  useEffect(() => {
    if (!selectedIcao) return;
    const ac =
      selectedTrack.aircraft ?? displayAircraft.find((a) => a.icao === selectedIcao) ?? live.aircraft.find((a) => a.icao === selectedIcao);
    if (!ac) return;

    const src = mapRef.current?.getSource('flight-trail') as maplibregl.GeoJSONSource | undefined;
    const pushTrail = () => src?.setData(trailToGeoJSON(trailRef.current));

    if (ac.onGround) {
      if (trailRef.current.length > 0) {
        trailRef.current = [];
        pushTrail();
      }
      selectedOnGroundRef.current = true;
      return;
    }

    const tookOff = selectedOnGroundRef.current === true;
    selectedOnGroundRef.current = false;

    if (tookOff) {
      trailRef.current = [];
      pushTrail();
      const reqId = ++traceRequestRef.current;
      traceAbortRef.current?.abort();
      const ctrl = new AbortController();
      traceAbortRef.current = ctrl;
      fetchTrace(selectedIcao, ctrl.signal)
        .then((trace) => {
          if (ctrl.signal.aborted || reqId !== traceRequestRef.current) return;
          trailRef.current = traceToTrail(trace.points);
          pushTrail();
        })
        .catch(() => {})
        .finally(() => {
          if (traceAbortRef.current === ctrl) traceAbortRef.current = null;
        });
    }

    if (ac.lat == null || ac.lon == null) return;
    const t = live.data?.time ?? Math.floor(Date.now() / 1000);
    trailRef.current = appendTrail(trailRef.current, ac.lat, ac.lon, t);
    pushTrail();
  }, [selectedIcao, displayAircraft, live.aircraft, live.data?.time, selectedTrack.aircraft]);

  useEffect(() => {
    vesselTrailRef.current = [];
    const src = mapRef.current?.getSource('vessel-trail') as maplibregl.GeoJSONSource | undefined;
    src?.setData(vesselTrailToGeoJSON([]));
    if (!selectedVesselId) return;
  }, [selectedVesselId]);

  useEffect(() => {
    if (!selectedVesselId) return;
    const v = liveVessels.vessels.find((x) => x.id === selectedVesselId);
    if (!v || v.lat == null || v.lon == null) return;

    const src = mapRef.current?.getSource('vessel-trail') as maplibregl.GeoJSONSource | undefined;
    const t = liveVessels.data?.time ?? Math.floor(Date.now() / 1000);
    vesselTrailRef.current = appendVesselTrail(vesselTrailRef.current, v.lat, v.lon, t);
    src?.setData(vesselTrailToGeoJSON(vesselTrailRef.current));
  }, [selectedVesselId, liveVessels.vessels, liveVessels.data?.time]);

  useEffect(() => {

    if (!containerRef.current) return;

    const map = new maplibregl.Map({

      container: containerRef.current,

      style: mapStyle(),

      center: [-79.4, 44.5],

      zoom: 6,

      maxZoom: 14,

      minZoom: 2,

      maxPitch: MAP_MAX_PITCH,

      attributionControl: { compact: true },

    });

    mapRef.current = map;
    mapBridge.setMap(map);

    map.addControl(
      new maplibregl.GeolocateControl({ positionOptions: { enableHighAccuracy: true }, trackUserLocation: true }),
      'bottom-left',
    );



    const pushView = () => {

      const b = map.getBounds();

      const view = {
        bbox: { minLon: b.getWest(), minLat: b.getSouth(), maxLon: b.getEast(), maxLat: b.getNorth() },
        zoom: map.getZoom(),
        globalFilter: globalFilterForClasses(filterRef.current.classes),
      };

      setViewRef.current(view);
      setVesselViewRef.current(view);

    };

    const updateBearing = () => mapBridge.setBearing(map.getBearing());
    const updatePitch = () => mapBridge.setPitch(map.getPitch());



    const selectFromEvent = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {

      const f = e.features?.[0];

      if (f?.properties) setSelected(String(f.properties['icao']));

    };

    const selectVesselFromEvent = (e: maplibregl.MapMouseEvent & { features?: maplibregl.MapGeoJSONFeature[] }) => {
      const f = e.features?.[0];
      if (f?.properties) setSelectedVessel(String(f.properties['id']));
    };

    const clearSelectionFromBackground = (e: maplibregl.MapMouseEvent) => {
      const aircraftLayers = ['aircraft-body', 'aircraft-selected', 'aircraft-label'].filter((id) => map.getLayer(id));
      if (map.getLayer('aircraft-icon')) aircraftLayers.push('aircraft-icon');
      for (const id of ALTITUDE_LAYER_IDS) {
        if (map.getLayer(id)) aircraftLayers.push(id);
      }
      const overlayLayers = [
        ...aircraftLayers,
        ...VESSEL_LAYER_IDS.filter((id) => map.getLayer(id)),
      ];
      const hits = map.queryRenderedFeatures(e.point, { layers: overlayLayers });
      if (hits.length > 0) return;
      if (mapBridge.canPick3DModels()) {
        const icao = pick3DRef.current(map, e.point);
        if (icao) {
          setSelected(icao);
          return;
        }
      }
      setSelected(null);
      setSelectedVessel(null);
    };

    const updatePointerCursor = (e: maplibregl.MapMouseEvent) => {
      if (!mapBridge.canPick3DModels()) return;
      const aircraftLayers = ['aircraft-body', 'aircraft-selected', 'aircraft-label'].filter((id) => map.getLayer(id));
      if (map.getLayer('aircraft-icon')) aircraftLayers.push('aircraft-icon');
      for (const id of ALTITUDE_LAYER_IDS) {
        if (map.getLayer(id)) aircraftLayers.push(id);
      }
      const hits = map.queryRenderedFeatures(e.point, { layers: aircraftLayers });
      const hover3d = hover3DRef.current(map, e.point);
      map.getCanvas().style.cursor = hits.length > 0 || hover3d ? 'pointer' : '';
    };



    map.on('load', async () => {
      ensureTerrainAndSky(map);

      map.addSource('flight-trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      map.addLayer({

        id: 'flight-trail-glow',

        type: 'line',

        source: 'flight-trail',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },

        paint: {

          'line-color': '#38bdf8',

          'line-width': 6,

          'line-opacity': 0.2,

          'line-blur': 2,

        },

      });

      map.addLayer({

        id: 'flight-trail',

        type: 'line',

        source: 'flight-trail',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },

        paint: {

          'line-color': '#7dd3fc',

          'line-width': 2.5,

          'line-opacity': 0.85,

        },

      });



      map.addSource('aircraft', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('flight-paths', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('aircraft-altitude', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      let iconsOk = false;

      try {

        iconsOk = await loadPlaneIcons(map);

      } catch {

        // circle markers still render

      }



      map.addLayer({

        id: 'flight-paths',

        type: 'line',

        source: 'flight-paths',

        paint: {

          'line-color': [

            'case',
            ['==', ['get', 'special'], 'presidential'],
            '#fde047',
            ['get', 'emergency'],
            '#ef4444',
            [
              'match',
              ['get', 'icon'],
              'military-fighter',
              '#fb923c',
              'military-transport',
              '#f97316',
              'military-other',
              '#fdba74',
              'military-helicopter',
              '#f97316',
              'private-jet',
              '#c4b5fd',
              'light-prop',
              '#a7f3d0',
              'helicopter',
              '#22d3ee',
              'balloon',
              '#f472b6',
              'government',
              '#fbbf24',
              '#7dd3fc',
            ],

          ],

          'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.75, 7, 1.25, 12, 2],

          'line-opacity': ['case', ['get', 'selected'], 0.7, 0.34],

          'line-dasharray': [1.5, 1.2],

        },

      });

      mapBridge.syncFlightPathsLayer();

      map.addSource('vessels', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('vessel-paths', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
      map.addSource('vessel-trail', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });

      let vesselIconsOk = false;
      try {
        vesselIconsOk = await loadVesselIcons(map);
      } catch {
        // circle fallback still renders
      }

      map.addLayer({
        id: 'vessel-paths',
        type: 'line',
        source: 'vessel-paths',
        paint: {
          'line-color': VESSEL_COLOR_EXPR as unknown as ExpressionSpecification,
          'line-width': ['interpolate', ['linear'], ['zoom'], 2, 0.75, 7, 1.25, 12, 2],
          'line-opacity': 0.5,
          'line-dasharray': [1.5, 1.2],
        },
      });

      map.addLayer({
        id: 'vessel-trail-glow',
        type: 'line',
        source: 'vessel-trail',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#2dd4bf',
          'line-width': 6,
          'line-opacity': 0.2,
          'line-blur': 2,
        },
      });

      map.addLayer({
        id: 'vessel-trail',
        type: 'line',
        source: 'vessel-trail',
        layout: {
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': '#5eead4',
          'line-width': 2.5,
          'line-opacity': 0.85,
        },
      });

      map.addLayer({
        id: 'vessel-body',
        type: 'circle',
        source: 'vessels',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 2, 6, 3, 9, 4, 12, 5],
          'circle-color': VESSEL_COLOR_EXPR as unknown as ExpressionSpecification,
          'circle-opacity': vesselIconsOk ? 0.15 : 0.85,
        },
      });

      if (vesselIconsOk) {
        map.addLayer({
          id: 'vessel-icon',
          type: 'symbol',
          source: 'vessels',
          minzoom: 2,
          layout: {
            'icon-image': VESSEL_ICON_IMAGE_EXPR as unknown as ExpressionSpecification,
            'icon-rotate': ['get', 'course'],
            'icon-rotation-alignment': 'map',
            'icon-size': VESSEL_ICON_SIZE_EXPR as unknown as ExpressionSpecification,
            'icon-allow-overlap': true,
          },
        });
        map.on('click', 'vessel-icon', selectVesselFromEvent);
        map.on('mouseenter', 'vessel-icon', () => (map.getCanvas().style.cursor = 'pointer'));
        map.on('mouseleave', 'vessel-icon', () => (map.getCanvas().style.cursor = ''));
      }

      map.addLayer({
        id: 'vessel-label',
        type: 'symbol',
        source: 'vessels',
        minzoom: 8,
        filter: ['==', ['get', 'selected'], true],
        layout: {
          'text-field': ['coalesce', ['get', 'label'], ['get', 'name'], ['get', 'id']],
          'text-font': MAP_TEXT_FONTS,
          'text-size': ['interpolate', ['linear'], ['zoom'], 8, 9, 12, 12],
          'text-offset': [0, 1.4],
          'text-anchor': 'top',
          'text-allow-overlap': false,
        },
        paint: {
          'text-color': '#ccfbf1',
          'text-halo-color': '#0b1020',
          'text-halo-width': 1.5,
        },
      });

      map.addLayer({
        id: 'vessel-selected',
        type: 'circle',
        source: 'vessels',
        filter: ['==', ['get', 'selected'], true],
        paint: {
          'circle-radius': 12,
          'circle-color': '#2dd4bf',
          'circle-stroke-width': 2,
          'circle-stroke-color': '#0b1020',
          'circle-opacity': 0.25,
        },
      });

      map.on('click', 'vessel-body', selectVesselFromEvent);
      map.on('mouseenter', 'vessel-body', () => (map.getCanvas().style.cursor = 'pointer'));
      map.on('mouseleave', 'vessel-body', () => (map.getCanvas().style.cursor = ''));

      mapBridge.syncVesselsLayer();

      // Altitude pillar = the aircraft silhouette footprint extruded up to
      // its cruise altitude (a "stretched" plane-shape column). The base is
      // a flat black `fill` ground shadow using the same silhouette polygon,
      // and the colored column is stacked just above it so the shadow stays
      // pure black instead of being washed out by the translucent extrusion.
      //   shadow: flat fill at z=0            (black)
      //   pillar: fill-extrusion base capSlice -> height (aircraft color)
      map.addLayer({
        id: 'aircraft-altitude-shadow',
        type: 'fill',
        source: 'aircraft-altitude',
        minzoom: 6,
        paint: {
          'fill-color': '#000000',
          'fill-opacity': 0.6,
        },
      });

      map.addLayer({
        id: 'aircraft-altitude-pillar',
        type: 'fill-extrusion',
        source: 'aircraft-altitude',
        minzoom: 6,
        paint: {
          'fill-extrusion-color': AIRCRAFT_COLOR_EXPR as unknown as ExpressionSpecification,
          'fill-extrusion-base': ALTITUDE_CAP_SLICE_EXPR as unknown as ExpressionSpecification,
          'fill-extrusion-height': ALTITUDE_HEIGHT_EXPR as unknown as ExpressionSpecification,
          'fill-extrusion-opacity': 0.4,
        },
      });

      mapBridge.syncHeightHologramsLayer();

      for (const id of ALTITUDE_LAYER_IDS) {
        map.on('click', id, selectFromEvent);
        map.on('mouseenter', id, () => (map.getCanvas().style.cursor = 'pointer'));
        map.on('mouseleave', id, () => (map.getCanvas().style.cursor = ''));
      }

      // 3D aircraft models floating on top of the altitude pillars. Reads the
      // same filtered list + extrapolation delta as the GeoJSON RAF loop so
      // the model stays aligned with the ground icon and the colored pillar.
      const modelLayer = createAircraftModelLayer({
        getState: () => ({
          list: filteredRef.current,
          snapshotTimeSec: snapshotTimeRef.current,
          nowSec: Math.floor(Date.now() / 1000),
          selectedIcao: selectedRef.current,
          selectedFetchedAtSec: selectedTrackRef.current.fetchedAtSec,
          preferLabelCallsign: preferLabelCallsignRef.current,
          show3DLabels: mapBridge.use3DLabels(),
          show3DPaths: mapBridge.use3DFlightPaths(),
        }),
        isHidden: () =>
          !mapBridge.getAircraft3DModelsEnabled() || mapBridge.getPitch() < MAP_TOP_DOWN_PITCH,
      });
      pick3DRef.current = modelLayer.pickAtPoint;
      hover3DRef.current = modelLayer.hasHoverAtPoint;
      map.addLayer(modelLayer.layer);
      mapBridge.syncAircraft3DModelsLayer();




      map.addLayer({

        id: 'aircraft-selected',

        type: 'circle',

        source: 'aircraft',

        filter: ['==', ['get', 'selected'], true],

        paint: {

          'circle-radius': 14,

          'circle-color': [
            'case',
            ['==', ['get', 'special'], 'presidential'],
            '#fde047',
            ['get', 'emergency'],
            '#ef4444',
            '#38bdf8',
          ],

          'circle-stroke-width': 2,

          'circle-stroke-color': '#0b1020',

          'circle-opacity': 0.25,

        },

      });



      map.addLayer({

        id: 'aircraft-body',

        type: 'circle',

        source: 'aircraft',

        paint: {

          'circle-radius': ['interpolate', ['linear'], ['zoom'], 2, 2, 6, 3, 9, 4, 12, 6],

          'circle-color': AIRCRAFT_COLOR_EXPR as unknown as ExpressionSpecification,

          'circle-stroke-width': iconsOk ? 0 : ['case', ['==', ['get', 'special'], 'presidential'], 3, ['get', 'emergency'], 3, 0],

          'circle-stroke-color': ['case', ['==', ['get', 'special'], 'presidential'], '#f8fafc', '#ef4444'],

          'circle-opacity': iconsOk ? ['case', ['get', 'selected'], 0.12, 0.01] : 0.9,

        },

      });



      if (iconsOk) {

        map.addLayer({

          id: 'aircraft-icon',

          type: 'symbol',

          source: 'aircraft',

          minzoom: 2,

          layout: {

            'icon-image': ICON_IMAGE_EXPR as unknown as ExpressionSpecification,

            'icon-rotate': ['coalesce', ['get', 'track'], 0],

            'icon-rotation-alignment': 'map',

            'icon-size': ICON_SIZE_EXPR as unknown as ExpressionSpecification,

            'icon-allow-overlap': true,

          },

        });

        map.on('click', 'aircraft-icon', selectFromEvent);

        mapBridge.syncGroundIconLayer();
      }



      map.addLayer({

        id: 'aircraft-label',

        type: 'symbol',

        source: 'aircraft',

        minzoom: 7,

        layout: {

          'text-field': preferLabelCallsign

            ? ['coalesce', ['get', 'callsign'], ['get', 'reg'], ['get', 'icao']]

            : ['get', 'icao'],

          'text-font': MAP_TEXT_FONTS,

          'text-size': ['interpolate', ['linear'], ['zoom'], 7, 9, 12, 12],

          'text-offset': [0, 1.4],

          'text-anchor': 'top',

          'text-allow-overlap': false,

        },

        paint: {

          'text-color': '#e6edf7',

          'text-halo-color': '#0b1020',

          'text-halo-width': 1.5,

        },

      });

      mapBridge.syncAircraftLabelLayer();

      pushView();

    });



    map.on('moveend', pushView);
    map.on('rotate', updateBearing);
    map.on('pitch', updatePitch);
    map.on('load', () => {
      updateBearing();
      updatePitch();
    });

    map.on('click', clearSelectionFromBackground);

    map.on('mousemove', updatePointerCursor);

    map.on('click', 'aircraft-body', selectFromEvent);

    map.on('mouseenter', 'aircraft-body', () => (map.getCanvas().style.cursor = 'pointer'));

    map.on('mouseleave', 'aircraft-body', () => (map.getCanvas().style.cursor = ''));



    const ro = new ResizeObserver(() => map.resize());

    ro.observe(containerRef.current);



    return () => {

      ro.disconnect();

      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      traceAbortRef.current?.abort();

      map.off('rotate', updateBearing);
      map.off('pitch', updatePitch);

      pick3DRef.current = () => null;
      hover3DRef.current = () => false;

      map.remove();

      mapRef.current = null;
      mapBridge.setMap(null);

    };

    // eslint-disable-next-line react-hooks/exhaustive-deps

  }, []);



  useEffect(() => {

    if (!mapRef.current) return;

    const map = mapRef.current;



    const frame = () => {

      const now = performance.now();

      const list = filteredRef.current;

      const interval = list.length > 2000 ? 500 : list.length > 500 ? 200 : 80;

      if (now - lastPaintRef.current >= interval) {

        lastPaintRef.current = now;

        const src = map.getSource('aircraft') as maplibregl.GeoJSONSource | undefined;
        const pathSrc = map.getSource('flight-paths') as maplibregl.GeoJSONSource | undefined;
        const altSrc = map.getSource('aircraft-altitude') as maplibregl.GeoJSONSource | undefined;
        const vesselSrc = map.getSource('vessels') as maplibregl.GeoJSONSource | undefined;
        const vesselPathSrc = map.getSource('vessel-paths') as maplibregl.GeoJSONSource | undefined;

        if (src || pathSrc || altSrc || vesselSrc || vesselPathSrc) {

          const nowSec = Math.floor(Date.now() / 1000);
          const track = selectedTrackRef.current;
          const vesselDtSec = Math.min(15, Date.now() / 1000 - vesselSnapshotTimeRef.current);
          const aircraftList = mapBridge.getAircraftEnabled() ? list : [];
          const vesselList = capVesselsForZoom(filteredVesselsRef.current, map.getZoom());
          const selectedVessel = selectedVesselRef.current;
          const vesselGeo = buildVesselsGeoJSON(vesselList, vesselDtSec, selectedVessel);
          const labelGeo = buildVesselLabelsGeoJSON(vesselList, vesselDtSec, selectedVessel);

          src?.setData(
            buildAircraftGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedRef.current, track.fetchedAtSec),
          );
          pathSrc?.setData(
            buildFlightPathGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedRef.current, track.fetchedAtSec),
          );
          altSrc?.setData(
            buildAircraftAltitudeGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedRef.current, track.fetchedAtSec),
          );
          vesselSrc?.setData(mergeVesselLabelText(vesselGeo, labelGeo));
          vesselPathSrc?.setData(buildVesselPathsGeoJSON(vesselList, vesselDtSec, selectedVessel));

        }

      }

      rafRef.current = requestAnimationFrame(frame);

    };

    rafRef.current = requestAnimationFrame(frame);

    return () => {

      if (rafRef.current) cancelAnimationFrame(rafRef.current);

    };

  }, []);



  useEffect(() => {

    const map = mapRef.current;

    if (!map?.getSource('aircraft')) return;

    const src = map.getSource('aircraft') as maplibregl.GeoJSONSource;
    const pathSrc = map.getSource('flight-paths') as maplibregl.GeoJSONSource | undefined;
    const altSrc = map.getSource('aircraft-altitude') as maplibregl.GeoJSONSource | undefined;
    const vesselSrc = map.getSource('vessels') as maplibregl.GeoJSONSource | undefined;
    const vesselPathSrc = map.getSource('vessel-paths') as maplibregl.GeoJSONSource | undefined;

    const nowSec = Math.floor(Date.now() / 1000);
    const vesselDtSec = Math.min(15, Date.now() / 1000 - vesselSnapshotTimeRef.current);
    const aircraftList = mapBridge.getAircraftEnabled() ? filtered : [];
    const vesselList = capVesselsForZoom(filteredVessels, map.getZoom());
    const vesselGeo = buildVesselsGeoJSON(vesselList, vesselDtSec, selectedVesselId);
    const labelGeo = buildVesselLabelsGeoJSON(vesselList, vesselDtSec, selectedVesselId);

    src.setData(
      buildAircraftGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedIcao, selectedTrack.fetchedAtSec),
    );
    pathSrc?.setData(
      buildFlightPathGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedIcao, selectedTrack.fetchedAtSec),
    );
    altSrc?.setData(
      buildAircraftAltitudeGeoJSON(aircraftList, snapshotTimeRef.current, nowSec, selectedIcao, selectedTrack.fetchedAtSec),
    );
    vesselSrc?.setData(mergeVesselLabelText(vesselGeo, labelGeo));
    vesselPathSrc?.setData(buildVesselPathsGeoJSON(vesselList, vesselDtSec, selectedVesselId));

  }, [filtered, filteredVessels, selectedIcao, selectedVesselId, selectedTrack.fetchedAtSec]);



  const count = filtered.length;
  const coverageStatus = live.data?.partial
    ? live.data.warmingTiles
      ? ` | warming ${live.data.warmingTiles}`
      : ' | partial'
    : '';
  const providerStatus =
    live.data?.providerErrors && live.data.providerErrors.length > 0 && count === 0
      ? ` · ${live.data.providerErrors[0]}`
      : '';



  return (

    <div className="absolute inset-0">

      <div ref={containerRef} className="absolute inset-0" />
      <StarrySky />

      <div className="pointer-events-none absolute bottom-[calc(var(--safe-bottom)+12px)] left-3 rounded-md bg-bg-panel/80 px-2 py-1 font-mono text-[11px] text-slate-300">

        {live.loading

          ? 'loading…'

          : `${count} shown${live.refreshing ? ' · updating' : ''}${coverageStatus} · ${live.data?.fromCache ? 'cache' : 'live'}`}

        {live.error ? <span className="text-bad"> · {live.error}</span> : null}
        {providerStatus ? <span className="text-bad">{providerStatus}</span> : null}

      </div>

    </div>

  );

}


